import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import styled from "styled-components";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "font-awesome/css/font-awesome.min.css";
import moment from "moment";
import { rest } from "lodash";

const CalendarComponent = forwardRef(({
  name,
  getSelectedDates,
  showCalendar = false,
  type,
  ...rest
}, ref) => {
  const [showFromDatePicker, setShowFromDatePicker] = useState(false);
  const [showToDatePicker, setShowToDatePicker] = useState(false);
  const [filterKey, setFilterKey] = useState(rest.filterKey);
  const [error, setError] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  useEffect(() => {
    setError(rest?.dateError)
  }, [rest?.dateError])
  // Expose clear method to parent component
  useImperativeHandle(ref, () => ({
    clearDates: () => {
      setFromDate(null);
      setToDate(null);
      setError("");
      setShowFromDatePicker(false);
      setShowToDatePicker(false);
    }
  }));

  const toggleFromDatePicker = () => {
    setShowFromDatePicker((prev) => !prev);
    if (!showFromDatePicker) setShowToDatePicker(false);
  };
  useEffect(() => {
    if (type == "qc_inbound_date_range") {
      if (rest?.value?.from && rest?.value?.to) {
        setFromDate(moment(rest.value.from).toDate());
        setToDate(moment(rest.value.to).toDate());
      } else if (!rest?.value?.from && !rest?.value?.to) {
        setFromDate(null);
        setToDate(null);
        setError("");
      }
    } else if (type == 'invoicedaterange') {

      if (rest?.value?.invoice_start_date && rest?.value?.invoice_end_date) {
        setFromDate(moment(rest.value.invoice_start_date).toDate());
        setToDate(moment(rest.value.invoice_end_date).toDate());
      } else {
        setFromDate(null);
        setToDate(null);
        setError("");
      }
    }
  }, [
    type,
    rest?.value?.from,
    rest?.value?.to,
    rest?.value?.invoice_start_date,
    rest?.value?.invoice_end_date
  ]);

 
  useEffect(() => {
    if (!showCalendar) {
      setShowFromDatePicker(false);
      setShowToDatePicker(false);
    }
  }, [showCalendar]);

  const toggleToDatePicker = () => {
    setShowToDatePicker((prev) => !prev);
  };

  const handleFromDateChange = (date) => {
    const selectedFromDate = new Date(date);
    setFromDate(selectedFromDate);
    setShowFromDatePicker(false);

    if (name === "qc_inbound_date_range" || (name && name.includes("qc_inbound"))) {
      if (toDate && toDate < selectedFromDate) {
        setToDate(null);
      }
    } else {
      const minDate = moment(date).subtract(30, "days").toDate();
      const maxDate = moment(date).add(30, "days").toDate();
      setToDate(minDate <= toDate && toDate <= maxDate ? toDate : null);
    }
  };

  const handleToDateChange = (date) => {
    if (!fromDate) {
      setError("Please select From Date first");
      return;
    }

    const minDate = moment(fromDate).subtract(30, "days").toDate();
    const maxDate = moment(fromDate).add(30, "days").toDate();
    if (name === "qc_inbound_date_range" || (name && name.includes("qc_inbound"))) {
      // For qc_inbound_date_range, allow any date from fromDate onwards (future dates)
      const parsedEndDate = rest.endDate ? moment(rest.endDate).toDate() : null;
      if (date < fromDate) {
        setError(`To Date must be on or after From Date`);
      } else if (parsedEndDate && date > parsedEndDate) {
        setError(`To Date cannot be after ${rest.endDate}`);
      } else {
        setError("");
        setToDate(date);
        setShowToDatePicker(false);
      }
      return;
    } else if (date >= minDate && date <= maxDate) {
      setToDate(date);
      setError("To Date must be within 30 days of From Date");
      setShowToDatePicker(false);
      return;
    }

    setError("");
    setToDate(date);
    setShowToDatePicker(false);
  }


  // Handle date submission
  const handleDateSubmission = () => {
    if (!fromDate) {
      setError("Please select from date");
      
    } else if (!toDate) {
      setError("Please select to date");
  
    } else {
      let formMomnet = moment(moment(fromDate).format("YYYY-MM-DD"))
      let toMomnet = moment(moment(toDate).format("YYYY-MM-DD"))

      if (formMomnet.isAfter(toMomnet)) {
        setError("From date should not after to date");
      } else {
        setError("");
      }
    }


    const dateRange = {
      from: fromDate == null ? fromDate : moment(fromDate).format("YYYY-MM-DD"),
      to: toDate == null ? toDate : moment(toDate).format("YYYY-MM-DD"),
    };

    getSelectedDates && getSelectedDates({ name, dateRange });
  };

  // Automatically submit date when To Date is selected
  useEffect(() => {
    if (!fromDate && !toDate) {
      return
    }
    handleDateSubmission();
  }, [fromDate, toDate]); 



  return (
    <CalendarContainer>
      <Label>From</Label>
      <DateSection>
        <InputWrapper>
          <span>
            {fromDate
              ? moment(fromDate).format("YYYY-MM-DD")
              : "Select a date"}
          </span>
          <i
            className="fa fa-calendar"
            onClick={toggleFromDatePicker}
            aria-hidden="true"
          ></i>
        </InputWrapper>
        {showFromDatePicker && (
          <StyledDatePicker>
            <DatePicker
              selected={fromDate}
              onChange={handleFromDateChange}
              dateFormat="yyyy/MM/dd"
              inline
              minDate={
                (name === "qc_inbound_date_range" || (name && name.includes("qc_inbound"))) && rest.startDate
                  ? moment(rest.startDate).toDate()
                  : null
              }
              maxDate={
                (name === "qc_inbound_date_range" || (name && name.includes("qc_inbound"))) && rest.endDate
                  ? moment(rest.endDate).toDate()
                  : new Date()
              }
            />
          </StyledDatePicker>
        )}
      </DateSection>

      <Label>To</Label>
      <DateSection>
        <InputWrapper>
          <span>
            {toDate
              ? moment(toDate).format("YYYY-MM-DD")
              : "Select a date"}
          </span>
          <i
            className="fa fa-calendar"
            onClick={toggleToDatePicker}
            aria-hidden="true"
          ></i>
        </InputWrapper>
        {showToDatePicker && (
          <StyledDatePicker>
            <DatePicker
              selected={toDate}
              onChange={handleToDateChange}
              dateFormat="yyyy/MM/dd"
              minDate={fromDate} // Ensure "toDate" cannot be before "fromDate"
              maxDate={
                (name === "qc_inbound_date_range" || (name && name.includes("qc_inbound")))
                  ? rest.endDate
                    ? moment(rest.endDate).toDate()
                    : new Date()
                  : fromDate
                    ? moment(fromDate).add(30, "days").toDate()
                    : null
              } // Limit to 30 days or no limit for qc_inbound_date_range
              inline
            />
          </StyledDatePicker>
        )}
      </DateSection>
      {error && <ErrorText>{error}</ErrorText>}
    </CalendarContainer>
  );
});

const CalendarContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 10px;
  width: 100%;
`;
const ErrorText = styled.span`
  color: #c60707;
  font-size: 12px;
  margin-top: -8px;
`;

const Label = styled.h4`
  margin: 0;
  color: #333;
  font-size: 14px;
  margin-bottom: 5px;
`;

const DateSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid #d4d4d4;
  border-radius: 4px;
  padding: 10px;
  position: relative;
`;

const InputWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  span {
    font-size: 13px;
    color: #666;
  }
  i {
    font-size: 16px;
    color: #333;
    cursor: pointer;
    transition: color 0.3s;
    &:hover {
      color: #007bff;
    }
  }
`;

const StyledDatePicker = styled.div`
  position: absolute;
  top: 40px;
  left: 0;
  right: 0;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: ${({ isOpen }) => (isOpen ? '10px' : '0')};  // Conditional margin-top
`;

export default CalendarComponent;
